import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"

import { Grid } from "@mui/material"
import {
  Chip,
  type InputChangeEvent,
  type InputOptionValue,
  InputText,
  Select,
  Typography,
} from "@synapse-analytics/synapse-ui"

import { useDebounce } from "../../hooks/useDebounce"
import { type FilterOptions, MenuFilter } from "../filters/MenuFilter"

import styles from "./SearchSortFilter.module.scss"

type Props = {
  searchValue: string | undefined
  onSearchValueChange: React.Dispatch<React.SetStateAction<string>>
  sortValue?: string
  onSortValueChange?: React.Dispatch<React.SetStateAction<string>>
  filterValue?: string[] | FilterOptions | null
  onFilterValueChange?:
    | React.Dispatch<React.SetStateAction<string[]>>
    | React.Dispatch<React.SetStateAction<FilterOptions>>
  containerType: string
  filterOptions?: string[] | FilterOptions
  sortOptions?: InputOptionValue[]
  disableSearch?: boolean
  isFilterLoading?: boolean
  isDebouncedInput?: boolean
  useMenuFilter?: boolean
}

/**
 * search sort filter component
 * @param  {string} searchValue
 * @param  {React.Dispatch} setSearchValue callback filter setting function
 * @param  {string} onSortValueChange value in the sort component
 * @param  {React.Dispatch} setSortValue callback filter setting function
 * @param  {string[]} filterValue
 * @param  {React.Dispatch} setFilterValue callback filter setting function
 * @param  {string} containerType to control placeholder and style accordingly
 * @param  {string[]} filterOptions
 * @param  {string[]} sortOptions
 * @param  {boolean} disableSearch option to disable search
 * @param  {boolean} isFilterLoading filter loading mode
 * @param  {boolean} useMenuFilter indicates which filter to use
 * @return {React.ReactElement}
 */
export function SearchSortFilter(props: Readonly<Props>): React.ReactElement {
  const {
    searchValue,
    sortValue,
    onSearchValueChange,
    onSortValueChange,
    filterValue,
    onFilterValueChange,
    containerType,
    filterOptions,
    sortOptions,
    disableSearch = false,
    isFilterLoading = false,
    isDebouncedInput,
    useMenuFilter = false,
  } = props

  const { t } = useTranslation(["form", "components"])

  // setting the search input placeholder based on the container
  const placeHolder =
    t(`containerTypes.${containerType}`, { ns: "form" }) + ` ${t("searchSortFilter.name", { ns: "form" })}`

  const [innerSearchValue, setInnerSearchValue] = useState<string>("")

  const onChange = (): void => {
    if (isDebouncedInput) {
      onSearchValueChange(innerSearchValue ?? "")
    }
  }

  const debouncedOnChange = useDebounce(onChange, 400)

  const handleChipClick = (item: string): void =>
    (onFilterValueChange as React.Dispatch<React.SetStateAction<string[]>>)?.(
      (filterValue as string[])?.includes(item)
        ? (filterValue as string[]).filter((elem) => elem !== item)
        : filterValue
          ? [...(filterValue as string[]), item]
          : [item],
    )

  return (
    <Grid container direction="row" className={containerType === "Model" ? styles.modMain : styles.depMain}>
      {/* Sort */}
      {sortOptions && 0 < sortOptions.length && (
        <div className={styles.compSpacing}>
          <Grid item xs={12}>
            <Select
              id="sort-by"
              label={t("searchSortFilter.sortBy", { ns: "form" })}
              optionsWithValues={sortOptions}
              value={sortValue}
              handleChange={(e: InputChangeEvent) => onSortValueChange?.(String(e.target.value))}
            />
          </Grid>
        </div>
      )}

      {/* Search */}
      {!disableSearch && (
        <div className={styles.compSpacing}>
          <InputText
            label={t("searchSortFilter.search", { ns: "form" })}
            id="search"
            value={isDebouncedInput ? innerSearchValue : searchValue}
            variant="filled"
            handleChange={(e: InputChangeEvent) => {
              const target = e.target

              if (isDebouncedInput) {
                debouncedOnChange()
                setInnerSearchValue(target.value as string)
              } else {
                onSearchValueChange(target.value as string)
              }
            }}
            placeholder={placeHolder}
            hideDescription
          />
        </div>
      )}

      {/* Filter */}
      {filterOptions && (
        <Grid item>
          {useMenuFilter ? (
            <MenuFilter onChange={onFilterValueChange} isGrouped options={filterOptions} value={filterValue} />
          ) : (
            <Fragment>
              <Grid item xs={12} mb={1}>
                <Typography variant="label-bold">{t("searchSortFilter.filter", { ns: "form" })}</Typography>
              </Grid>
              <Grid container direction="row" padding={0} gap={1}>
                {Array.isArray(filterOptions) &&
                  Array.isArray(filterValue) &&
                  0 < filterOptions.length &&
                  filterOptions.map((item: string, index) => (
                    <Grid item padding={0} key={index}>
                      <Chip
                        isSelected={filterValue?.includes(item)}
                        onClick={() => handleChipClick(item)}
                        clickable={true}
                        disabled={isFilterLoading}
                        id={`${item.replace(" ", "_")}_chip`}
                      >
                        {t(
                          `MenuFilter.options.${item.toLowerCase().charAt(0).toUpperCase() + item.toLowerCase().slice(1)}`,
                          {
                            ns: "components",
                          },
                        )}
                      </Chip>
                    </Grid>
                  ))}
              </Grid>
            </Fragment>
          )}
        </Grid>
      )}
    </Grid>
  )
}
